import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShoppingBag, Heart, Users, Sparkles, Briefcase,
  MapPin, Phone, Mail, MessageCircle, Facebook
} from 'lucide-react';
import { apiFetch } from '../lib/api';
import SiteHeader from '../components/SiteHeader';
import LeverageSection from '../components/LeverageSection';
import TestimonialsSection from '../components/TestimonialsSection';
import StudentStoriesSection from '../components/StudentStoriesSection';
import MediaCoverageSection from '../components/MediaCoverageSection';

export interface HeroSection {
  type: 'hero';
  title: string;
  subtitle: string;
  description: string;
  backgroundImage: string;
  ctaPrimary: { label: string; url: string };
  ctaSecondary: { label: string; url: string };
}

export interface MissionSection {
  type: 'mission';
  title: string;
  description: string;
  values: { title: string; description: string }[];
}

export interface AboutSection {
  type: 'about';
  title: string;
  description: string;
  imageUrl: string;
  imageAlt: string;
}

export interface VocationalTrainingSection {
  type: 'vocationalTraining';
  title: string;
  description: string;
  logoUrl: string;
  features: string[];
  imageUrl: string;
}

export interface CorporateSupportSection {
  type: 'corporateSupport';
  title: string;
  description: string;
  imageUrl: string;
}

export interface MediaCoverageSection {
  type: 'mediaCoverage';
  title: string;
  description: string;
  items: { imageUrl: string; caption: string }[];
}

export interface SuccessStoriesSection {
  type: 'successStories';
  title: string;
  quotes: { quote: string; author: string }[];
}

export interface FooterSection {
  type: 'footer';
  logoUrl: string;
  address: string;
  phone: string;
  whatsapp: string;
  email: string;
  facebookUrl: string;
  copyright: string;
  facebookIcon: string;
}

export interface LeverageSectionData {
  type: 'leverage';
  badge: string;
  title: string;
  description: string;
  leftImageUrl: string;
  rightImageUrl: string;
  leftTitle: string;
  leftDescription: string;
  rightTitle: string;
  rightDescription: string;
  beforeLabel: string;
  afterLabel: string;
}

export type BrandStorySection =
  | HeroSection
  | MissionSection
  | AboutSection
  | VocationalTrainingSection
  | CorporateSupportSection
  | MediaCoverageSection
  | SuccessStoriesSection
  | FooterSection
  | LeverageSectionData;

export interface BrandStoryPage {
  title: string;
  description: string;
  sections: BrandStorySection[];
  meta?: Record<string, any>;
  isActive?: boolean;
}

export const DEFAULT_BRAND_STORY_SECTIONS: BrandStorySection[] = [
  {
    type: 'hero',
    title: '<b style="display:block; font-size:0.4em; font-weight:500; letter-spacing:0.05em; margin-bottom:0.4rem;">WELCOME TO ALEX KITCHEN</b> <b style="display:block;">主廚嚴選 · 辣得起 · 有質素</b>',
    subtitle: '阿力廚房 by Chef Alex',
    description: '私房菜主廚 Alex 用多年廚藝同網絡，親自為你揀選真正值得入廚房的好食材。皇牌 X1493 真辣系列，九年匠心之作，由廚房直送到你屋企。\n滿 HK$350 起即安排快遞配送 · FPS / PayMe 付款。',
    backgroundImage: '/media/story/hero.jpg',
    ctaPrimary: { label: '瀏覽主廚推介', url: '/' },
    ctaSecondary: { label: '聯絡我們', url: '#contact' },
  },
  {
    type: 'mission',
    title: '主廚嚴選，質素為先',
    description: '網上選購食材，品質往往難以掌握。阿力廚房每一件產品，均由私房菜主廚 Alex 親自挑選與試用，確保達到私廚廚房的使用水準。\n\n從皇牌 X1493 真辣系列、手工辣椒油，到各款優質食材，我們堅持只選能夠煮出好菜的貨品，不求量多，但求對味。',
    values: [
      { title: '主廚把關', description: 'Alex 親自把關好食材，確保每件產品都達到私廚級水準。' },
      { title: '九年口碑', description: '皇牌 X1493 真辣系列自 2016 年起堅持小批量製作，辣得天然有層次。' },
      { title: '香港本地', description: '本地出品、新鮮製作，滿 HK$350 起即可安排港九新界快遞配送。' },
    ],
  },
  {
    type: 'about',
    title: '關於 Chef Alex',
    description: 'Alex 係一位香港私房菜主廚，多年來為食客炮製精緻菜式，對食材同辣味有近乎固執的堅持。\n\n2016 年，他創立 X-1493 真辣系列，由一款天然真辣粉開始，做到今日成為不少識食之人廚房必備的調味品。九年過去，X1493 依然堅持小批量製作、香港本地出品，辣得天然、辣得有層次。\n\n而家 Alex 透過阿力廚房，將自己多年累積的廚師網絡同嚴選眼光帶到網上——<span class="text-[#C2410C] font-bold text-lg">唔係乜都賣，淨係賣佢真心推介、自己都用開嘅好嘢。</span>',
    imageUrl: '/media/story/chef-alex.jpg',
    imageAlt: 'Chef Alex',
  },
  {
    type: 'leverage',
    badge: '主廚揀 · 團購價 · 送到門',
    title: '一般網購食材 vs 主廚嚴選',
    description: '網上買食材，最怕貨不對辦。阿力廚房由私廚主廚 Alex 親自把關，用多年廚師網絡為你揀選優質食材同調味品，再以團購價直送上門，等識食之人買得安心、煮得出色。',
    leftImageUrl: '/media/story/hero.jpg',
    rightImageUrl: '/media/story/chef-alex.jpg',
    leftTitle: '一般網購食材',
    leftDescription: '來源不明、質素參差、買完唔知點用。',
    rightTitle: '主廚嚴選',
    rightDescription: '由私房菜主廚 Alex 親自把關；皇牌 X1493 九年口碑；團購價優惠，直送港九新界。',
    beforeLabel: '一般網購：貨不對辦',
    afterLabel: '阿力廚房：主廚把關 · 團購價',
  },
  {
    type: 'footer',
    logoUrl: '/logo.jpg',
    address: '新界葵涌永健路16至20號高威工業中心A座501室',
    phone: '60771493',
    whatsapp: '60771493',
    email: 'support@alexkitchens.com',
    facebookUrl: 'https://www.facebook.com/profile.php?id=100064050482929',
    copyright: '© 2026 阿力廚房 Alex Kitchen. All rights reserved.',
    facebookIcon: '/media/story/facebook-icon.png',
  },
];

function classNames(...classes: (string | false | undefined)[]) {
  return classes.filter(Boolean).join(' ');
}

function renderHtmlDescription(text?: string) {
  if (!text) return null;
  const html = text
    .split(/\n\s*\n/)
    .map(p => p.trim())
    .filter(Boolean)
    .map(p => `<p class="mb-4 last:mb-0">${p}</p>`)
    .join('');
  return <div dangerouslySetInnerHTML={{ __html: html }} />;
}

function SafeImage({
  src,
  alt,
  className,
  fallbackClassName,
}: {
  src?: string;
  alt: string;
  className?: string;
  fallbackClassName?: string;
}) {
  const [error, setError] = React.useState(false);
  if (!src || error) {
    return (
      <div
        className={classNames(
          'flex items-center justify-center bg-gray-100 text-gray-400',
          fallbackClassName || className
        )}
      >
        <span className="text-xs">{alt}</span>
      </div>
    );
  }
  return <img src={src} alt={alt} className={className} onError={() => setError(true)} />;
}

const BrandStory: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState<BrandStoryPage | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const res = await apiFetch('/api/public/cms/pages/brand-story');
        if (res.ok) {
          const data = await res.json();
          if (data && Array.isArray(data.sections) && data.sections.length > 0) {
            setPage({
              title: data.title || '品牌故事',
              description: data.description || '',
              sections: data.sections,
              meta: data.meta || {},
              isActive: data.isActive ?? true,
            });
          } else {
            setPage({
              title: data.title || '品牌故事',
              description: data.description || '',
              sections: DEFAULT_BRAND_STORY_SECTIONS,
              meta: data.meta || {},
              isActive: data.isActive ?? true,
            });
          }
        } else {
          setPage({
            title: '品牌故事',
            description: '',
            sections: DEFAULT_BRAND_STORY_SECTIONS,
            meta: {},
            isActive: true,
          });
        }
      } catch {
        setPage({
          title: '品牌故事',
          description: '',
          sections: DEFAULT_BRAND_STORY_SECTIONS,
          meta: {},
          isActive: true,
        });
      } finally {
        setLoading(false);
      }
    };
    fetchPage();
  }, []);

  useEffect(() => {
    if (page?.title) {
      document.title = `${page.title} | 阿力廚房 Alex Kitchen`;
    }
  }, [page?.title]);

  const sections = page?.sections || DEFAULT_BRAND_STORY_SECTIONS;

  const renderDescription = (text?: string) => {
    if (!text) return null;
    return text.split('\n').map((line, i) => (
      <p key={i} className={classNames(i > 0 && 'mt-3')}>
        {line}
      </p>
    ));
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5] text-stone-800">
      <SiteHeader />

      {loading ? (
        <div className="flex flex-col items-center justify-center py-24 text-stone-500">
          <div className="w-10 h-10 border-4 border-orange-200 border-t-[#C2410C] rounded-full animate-spin" />
          <p className="mt-4">載入中...</p>
        </div>
      ) : (
        <main>
          {sections.map((section, idx) => {
            // Skip legacy CMS sections that are now handled by dynamic components or removed.
            if (section.type === 'mediaCoverage') return null;
            if (section.type === 'successStories') return null;

            switch (section.type) {
              case 'hero': {
                const s = section as HeroSection;
                return (
                  <section
                    key={idx}
                    className="relative w-full aspect-video bg-brand-700 text-white overflow-hidden"
                  >
                    <div className="absolute inset-0">
                      <SafeImage
                        src={s.backgroundImage}
                        alt="品牌故事背景"
                        className="w-full h-full object-cover opacity-40"
                        fallbackClassName="w-full h-full"
                      />
                      <div className="absolute inset-0 bg-gradient-to-r from-brand-900/80 to-brand-700/40" />
                    </div>
                    <div className="relative max-w-6xl mx-auto px-4 py-16 md:py-24">
                      <div className="max-w-2xl">
                        <p className="text-brand-100 text-sm md:text-base font-medium mb-3 tracking-wider">
                          {s.subtitle}
                        </p>
                        <h1 className="text-3xl md:text-5xl font-bold mb-6 leading-tight">
                          <span dangerouslySetInnerHTML={{ __html: s.title }} />
                        </h1>
                        <div className="text-base md:text-lg text-white/90 leading-relaxed mb-8">
                          {renderDescription(s.description)}
                        </div>
                        <div className="flex flex-wrap gap-4">
                          <a
                            href={s.ctaPrimary.url}
                            className="inline-flex items-center gap-2 px-6 py-3 bg-white text-brand-700 font-bold rounded-full hover:bg-brand-50 transition-colors shadow-lg"
                          >
                            <ShoppingBag size={18} />
                            {s.ctaPrimary.label}
                          </a>
                          <a
                            href={s.ctaSecondary.url}
                            className="inline-flex items-center gap-2 px-6 py-3 border-2 border-white/80 text-white font-bold rounded-full hover:bg-white/10 transition-colors"
                          >
                            {s.ctaSecondary.label}
                          </a>
                        </div>
                      </div>
                    </div>
                  </section>
                );
              }

              case 'mission': {
                const s = section as MissionSection;
                return (
                  <section key={idx} className="py-16 md:py-24 px-4 bg-[#FFF7ED]">
                    <div className="max-w-6xl mx-auto">
                      <div className="text-center max-w-3xl mx-auto mb-12">
                        <div className="inline-flex items-center gap-2 text-brand-600 font-medium mb-4">
                          <Heart size={20} />
                          <span>我們相信</span>
                        </div>
                        <h2 className="text-2xl md:text-4xl font-bold text-stone-800 mb-6">
                          {s.title}
                        </h2>
                        <div className="text-stone-600 leading-relaxed">
                          {renderDescription(s.description)}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {s.values.map((v, i) => (
                          <div
                            key={i}
                            className="bg-[#FFFBF5] rounded-2xl p-6 md:p-8 border border-stone-100 hover:shadow-lg transition-shadow"
                          >
                            <div className="w-12 h-12 bg-brand-100 rounded-xl flex items-center justify-center mb-4">
                              <Sparkles className="text-brand-600" size={24} />
                            </div>
                            <h3 className="text-lg font-bold text-stone-800 mb-3">
                              {v.title}
                            </h3>
                            <p className="text-stone-600 text-sm leading-relaxed">
                              {v.description}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </section>
                );
              }

              case 'about': {
                const s = section as AboutSection;
                return (
                  <section key={idx} className="py-16 md:py-24 px-4 bg-[#FFFBF5]">
                    <div className="max-w-6xl mx-auto">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                        <div className="order-2 lg:order-1">
                          <div className="inline-flex items-center gap-2 text-brand-600 font-medium mb-4">
                            <Users size={20} />
                            <span>關於我們</span>
                          </div>
                          <h2 className="text-2xl md:text-4xl font-bold text-stone-800 mb-6">
                            {s.title}
                          </h2>
                          <div className="text-stone-600 leading-relaxed">
                            {renderHtmlDescription(s.description)}
                          </div>
                        </div>
                        <div className="order-1 lg:order-2">
                          <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-xl bg-white">
                            <SafeImage
                              src={s.imageUrl}
                              alt={s.imageAlt}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>
                );
              }

              case 'vocationalTraining': {
                const s = section as VocationalTrainingSection;
                return (
                  <section key={idx} className="py-16 md:py-24 px-4 bg-[#FFF7ED]">
                    <div className="max-w-6xl mx-auto">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                        <div>
                          <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-xl bg-white mb-6 lg:mb-0">
                            <SafeImage
                              src={s.imageUrl}
                              alt={s.title}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        </div>
                        <div>
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-14 h-14 bg-white rounded-xl shadow-sm p-1.5 overflow-hidden flex-shrink-0">
                              <SafeImage
                                src={s.logoUrl}
                                alt="阿力廚房標誌"
                                className="w-full h-full object-contain"
                              />
                            </div>
                            <h2 className="text-2xl md:text-4xl font-bold text-stone-800">
                              {s.title}
                            </h2>
                          </div>
                          <div className="text-stone-600 leading-relaxed mb-8">
                            {renderDescription(s.description)}
                          </div>
                          <ul className="space-y-4">
                            {s.features.map((feature, i) => (
                              <li key={i} className="flex items-start gap-3">
                                <span className="mt-1 w-6 h-6 rounded-full bg-brand-100 flex items-center justify-center flex-shrink-0">
                                  <Sparkles size={14} className="text-brand-600" />
                                </span>
                                <span className="text-stone-700 text-sm md:text-base leading-relaxed">
                                  {feature}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </section>
                );
              }

              case 'corporateSupport': {
                const s = section as CorporateSupportSection;
                return (
                  <section key={idx} className="py-16 md:py-24 px-4 bg-[#FFFBF5]">
                    <div className="max-w-6xl mx-auto">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                        <div>
                          <div className="inline-flex items-center gap-2 text-brand-600 font-medium mb-4">
                            <Briefcase size={20} />
                            <span>企業伙伴</span>
                          </div>
                          <h2 className="text-2xl md:text-4xl font-bold text-stone-800 mb-6">
                            {s.title}
                          </h2>
                          <div className="text-stone-600 leading-relaxed">
                            {renderDescription(s.description)}
                          </div>
                        </div>
                        <div>
                          <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-xl bg-white">
                            <SafeImage
                              src={s.imageUrl}
                              alt={s.title}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>
                );
              }

              case 'leverage': {
                const s = section as LeverageSectionData;
                return (
                  <LeverageSection
                    key={idx}
                    badge={s.badge}
                    title={s.title}
                    description={s.description}
                    leftImageUrl={s.leftImageUrl}
                    rightImageUrl={s.rightImageUrl}
                    leftTitle={s.leftTitle}
                    leftDescription={s.leftDescription}
                    rightTitle={s.rightTitle}
                    rightDescription={s.rightDescription}
                    beforeLabel={s.beforeLabel}
                    afterLabel={s.afterLabel}
                  />
                );
              }

case 'footer': {
                const s = section as FooterSection;
                return (
                  <React.Fragment key={idx}>
                    <TestimonialsSection />
                    <StudentStoriesSection />
                    <MediaCoverageSection />
                    <footer id="contact" className="bg-stone-900 text-stone-100 py-12 px-4">
                      <div className="max-w-6xl mx-auto">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-10">
                          <div className="lg:col-span-1">
                            <SafeImage
                              src={s.logoUrl}
                              alt="阿力廚房 Alex Kitchen"
                              className="mb-4 h-auto max-h-20 w-auto object-contain"
                            />
                            <p className="text-sm text-stone-300 leading-relaxed">
                              Alex 主廚親自把關好食材，以優惠團購價將好東西帶俾一眾同好，滿 HK$350 起即可安排快遞直送。
                            </p>
                          </div>
                          <div>
                            <h3 className="font-bold text-white mb-4">聯絡我們</h3>
                            <ul className="space-y-3 text-sm">
                              <li className="flex items-start gap-3">
                                <MapPin size={16} className="mt-0.5 text-brand-400 flex-shrink-0" />
                                <span>{s.address}</span>
                              </li>
                              <li className="flex items-center gap-3">
                                <Phone size={16} className="text-brand-400 flex-shrink-0" />
                                <span>{s.phone}</span>
                              </li>
                              <li className="flex items-center gap-3">
                                <MessageCircle size={16} className="text-brand-400 flex-shrink-0" />
                                <a
                                  href={`https://wa.me/852${s.whatsapp.replace(/\D/g, '')}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="hover:text-brand-400 transition-colors"
                                >
                                  WhatsApp {s.whatsapp}
                                </a>
                              </li>
                              <li className="flex items-center gap-3">
                                <Mail size={16} className="text-brand-400 flex-shrink-0" />
                                <a
                                  href={`mailto:${s.email}`}
                                  className="hover:text-brand-400 transition-colors"
                                >
                                  {s.email}
                                </a>
                              </li>
                            </ul>
                          </div>
                          <div>
                            <h3 className="font-bold text-white mb-4">追蹤我們</h3>
                            <a
                              href={s.facebookUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 px-4 py-2 bg-[#1877F2] text-white rounded-lg hover:bg-[#166fe5] transition-colors"
                            >
                              {s.facebookIcon ? (
                                <img src={s.facebookIcon} alt="Facebook" className="w-5 h-5" />
                              ) : (
                                <Facebook size={18} />
                              )}
                              <span>Facebook</span>
                            </a>
                          </div>
                          <div>
                            <h3 className="font-bold text-white mb-4">快速連結</h3>
                            <ul className="space-y-2 text-sm">
                              <li>
                                <button
                                  onClick={() => navigate('/')}
                                  className="text-stone-300 hover:text-white transition-colors"
                                >
                                  立即選購
                                </button>
                              </li>
                              <li>
                                <button
                                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                                  className="text-stone-300 hover:text-white transition-colors"
                                >
                                  回到頂部
                                </button>
                              </li>
                            </ul>
                          </div>
                        </div>
                        <div className="pt-8 border-t border-stone-700 text-center text-xs text-stone-400">
                          Powered by{' '}
                          <a
                            href="https://jkdcoding.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-semibold hover:underline"
                            style={{ color: '#CCFF00' }}
                          >
                            JKDCoding
                          </a>
                        </div>
                      </div>
                    </footer>
                  </React.Fragment>
                );
              }

              default:
                return null;
            }
          })}
        </main>
      )}
    </div>
  );
};

export default BrandStory;
